import {
  isBlankMessageContent,
  TEAMMATE_CONNECTOR_SURFACE,
  TEAMMATE_CONNECTOR_TRAITS,
} from '@shared';

import type { FileUploadDisabledResult } from '../../shared/selectors/conversation-prompt.selectors';
import { hasMessages } from '../../store/chat-lifecycle.helper';
// Type-only: the store imports nothing from the composer.
import type { ChatWithMessages } from '../../store/chats.reducer';

/**
 * What the composer sends for a typed input (SB-3538 T4). Blank input —
 * whitespace, or only zero-width and other invisible characters — is sent as
 * exactly `''`: the user bubble hides only on empty content, and the API's
 * empty-send rule reads blank the same way. Anything visible is sent as typed.
 */
export const toSendableMessageContent = (input: string): string =>
  isBlankMessageContent(input) ? '' : input;

/** Whether the send is blocked because text is required and none is visible. */
export const isPromptTextMissing = (
  required: boolean,
  input: string,
): boolean => required && isBlankMessageContent(input);

/** The chat fields the Teammate host's gates read. */
type TeammateHostChat = Pick<
  ChatWithMessages,
  'teammateId' | '_count' | 'messages' | 'isDraft'
>;

/**
 * Whether the Teammate host may send now (SB-3978): only into the empty chat
 * started for this page's Teammate. It is false for a stale selection, no
 * chat, a chat that already has messages, and a chat for another Teammate.
 * The conversation host is never gated here.
 */
export const teammateHostMaySend = (
  host: 'conversation' | 'teammate',
  pageTeammateId: string | null,
  chat: TeammateHostChat | null,
): boolean =>
  host !== 'teammate' ||
  (!!chat &&
    !hasMessages(chat) &&
    !!pageTeammateId &&
    chat.teammateId === pageTeammateId);

/**
 * Upload on the Teammate host follows the send gate (SB-3978 B4-T17). A
 * finished upload attaches to whatever chat is selected, so the Teammate page
 * takes files only into the empty chat started for its Teammate: the same
 * predicate as send, never a second definition. Elsewhere, and once the gate
 * holds, the selector's result stands. `CHAT.LOADING` is the key the send gate
 * shows for the same state.
 */
export const teammateHostFileUploadDisabled = (
  host: 'conversation' | 'teammate',
  pageTeammateId: string | null,
  chat: TeammateHostChat | null,
  fromSelector: FileUploadDisabledResult,
): FileUploadDisabledResult =>
  host === 'teammate' && !teammateHostMaySend(host, pageTeammateId, chat)
    ? {
        disabled: true,
        i18nKey: 'CHAT.LOADING',
        i18nParams: {},
        reason: 'The Teammate chat is not ready for files',
      }
    : fromSelector;

/** The input's placeholder: a translation key and its params. */
export const promptPlaceholder = (
  sttState: string,
  trait: string | null | undefined,
  isNewWithAssistant: boolean,
  teammateName: string | undefined,
): { key: string; params: Record<string, string> } => {
  switch (sttState) {
    case 'starting':
      return { key: 'PROMPT_INPUT.STT_STARTING', params: {} };
    case 'transcripting':
      return { key: 'PROMPT_INPUT.STT_LISTENING', params: {} };
    case 'finalizing':
      return { key: 'PROMPT_INPUT.STT_FINALIZING', params: {} };
    default:
      if (teammateName !== undefined) {
        return {
          key: 'PROMPT_INPUT.PLACEHOLDER_TEAMMATE',
          params: { name: teammateName },
        };
      }
      return {
        key:
          trait === 'comparison' && isNewWithAssistant
            ? 'ASSISTANTS.TRAITS.COMPARISON.PLACEHOLDER_INPUT_TEXT'
            : 'PROMPT_INPUT.PLACEHOLDER',
        params: {},
      };
  }
};

/** The chat field the connector helpers read. */
type ConnectorChat = { teammateId?: string | null } | null;

/**
 * The connector surface the composer filters rows by (SB-3978 B7-T24, KD-32).
 * A Teammate chat, server or draft, has no "skill that answers" (KD-46), so
 * it is `teammate` whenever the chat names a Teammate; otherwise the
 * answering skill's trait, or `general`.
 */
export const composerConnectorSurface = (
  chat: ConnectorChat,
  skillTrait: string | undefined,
): string =>
  chat?.teammateId ? TEAMMATE_CONNECTOR_SURFACE : (skillTrait ?? 'general');

/**
 * Whether the composer offers connector switches (SB-3978 B7-T24; XXII gap
 * (e), decision (a)). Always on general and skill chats. On a Teammate chat,
 * only when its skills hold a trait whose turns carry tokens; an unresolved
 * Teammate hides them. Display only: the API alone decides what is sent, and
 * the saved switched-off set applies either way.
 */
export const composerShowsConnectors = (
  chat: ConnectorChat,
  teammateSkillTraits: readonly string[] | undefined,
): boolean =>
  !chat?.teammateId ||
  !!teammateSkillTraits?.some((trait) =>
    TEAMMATE_CONNECTOR_TRAITS.includes(trait),
  );
